/**
 * Tab "Good Allocation" của /purchase/control — Access `frmProcurementMainSubFormGoodAlc`.
 * List (frmProcurementQryGoodAlc → query CTE procurement_goodsallocation → public.v_goods_allocation)
 * + toolbar 8 nút (theo Left):
 *   New · Edit · Copy · Truck Status · Tick All · Delete · Mass Upload · Print BBGN.
 * Template dataList chuẩn (/convert-table): FilterBar + Search + per-column ▽ +
 * Columns menu (persist `procurement`/columns/procurement-good-allocation) + freeze
 * contract_no + row-select toggle (Edit/Copy/single-Delete) + tick-checkbox cột đầu
 * (Access checked_row) cho thao tác multi-row (Tick All / Truck Status / multi-Delete).
 * Access Form_Load GoToRecord acLast → auto-select DÒNG ĐẦU (id cao nhất, list DESC).
 */
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import PrintIcon from '@mui/icons-material/Print'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'

import {
  useListGoodAllocationsQuery,
  useDeleteGoodAllocationMutation,
  useDeleteManyGoodAllocationsMutation,
} from '../../store/api/procurementApi'
import type { GoodAllocationListItem } from '../../types/api'
import type { ProcurementFilters } from '@/pages/procurement/ProcurementPage'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  DataframeBody,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../components/dataList'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import ResizeHandle from '../../components/ResizeHandle'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '../../utils/datasheetColumns/usePreferenceColumns'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { titleCaseName } from '../../utils/text'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import GoodAllocationFormDialog from './GoodAllocationFormDialog'
import GoodAllocationMassUploadDialog from './GoodAllocationMassUploadDialog'
import GoodAllocationTruckStatusDialog from './GoodAllocationTruckStatusDialog'
import GoodAllocationBbgnPrintDialog from './GoodAllocationBbgnPrintDialog'

interface Props {
  filters: ProcurementFilters
}

type FilterKind = 'search'
type ColumnKind = 'text' | 'date' | 'datetime' | 'number' | 'int'
type ColumnDef = {
  key: keyof GoodAllocationListItem & string
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
}

const ALL_ROWS_PAGE_SIZE = 1_000_000

const FROZEN_KEYS: ReadonlySet<string> = new Set(['contract_no'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { contract_no: 130 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { contract_no: 120 }

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const TICK_COL_WIDTH = 44

// Cột tên riêng nguồn cp_vn — DB lưu IN HOA → render Title Case (feedback_proper_case_cell).
// KHÔNG áp cho code/email/grade/inspection-abbrev.
const NAME_KEYS: ReadonlySet<string> = new Set(['supplier', 'name', 'short_name', 'rep'])

// Thứ tự + caption theo `ColumnOrder` của frmProcurementQryGoodAlc (checked_row
// ColumnOrder=0 → cột tick riêng, không trong TABLE_COLUMNS).
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'ID', kind: 'int', align: 'right', width: 76 }, // 1 (ID integer, no thousand sep, not summed)
  { key: 'contract_no', label: 'Contract No', width: 130 }, // 2 (frozen)
  { key: 'cp_code', label: 'Supplier Code', width: 120 }, // 3
  { key: 'supplier', label: 'Supplier', wrap: true, width: 400 }, // 4
  { key: 'contract_quality', label: 'Contract Quality', width: 132 }, // 5
  { key: 'm_date', label: 'M Date', kind: 'date', width: 128 }, // 6
  { key: 'no_bag', label: 'No Bags', kind: 'number', align: 'right', width: 90 }, // 7
  { key: 'm_truck_no', label: 'M Truck No', width: 180 }, // 8
  { key: 'estimated_quantity', label: 'Estimated Quantity', kind: 'number', align: 'right', width: 150 }, // 9
  { key: 'grade', label: 'Grade', filter: 'checkboxes', width: 90 }, // 10
  { key: 'inspection', label: 'Inspection', filter: 'checkboxes', width: 110 }, // 11
  { key: 'phone_no', label: 'Phone Number', width: 126 }, // 12
  { key: 'to_warehouse', label: 'To Warehouse', width: 180 }, // 13
  { key: 'truck_status', label: 'Truck Status', filter: 'checkboxes', width: 114 }, // 14
  { key: 'truck_id', label: 'Truck ID', align: 'right', width: 90 }, // 15
  { key: 'received_bag', label: 'Received (Bag)', kind: 'number', align: 'right', width: 126 }, // 16
  { key: 'receipt_no', label: 'Receipt No', width: 170 }, // 17
  { key: 'grn_or_rdn', label: 'GRN or RDN', filter: 'checkboxes', width: 112 }, // 18
  { key: 'note', label: 'Note', wrap: true, width: 200 }, // 19
  { key: 'date_in', label: 'Date', width: 100 }, // 20 (varchar — 'Recheck'/date string)
  { key: 'receipt_quality', label: 'Receipt Quality', width: 128 }, // 21
  { key: 'net_weight', label: 'Net Weight', width: 106 }, // 22 (varchar)
  { key: 'payment_qty', label: 'Payment Qty', kind: 'number', align: 'right', width: 114 }, // 23
  { key: 'truck_no', label: 'Truck No', width: 110 }, // 24
  { key: 'balance', label: 'Balance', kind: 'number', align: 'right', width: 100 }, // 25
  { key: 'received_kg', label: 'Received (Kg)', kind: 'number', align: 'right', width: 120 }, // 26
  { key: 'tobe_received_bag', label: 'To be Received (Bag)', kind: 'number', align: 'right', width: 160 }, // 27
  { key: 'contract_type', label: 'ContractType', filter: 'checkboxes', width: 116 }, // 28
  { key: 'tobe_received_kg', label: 'To be Received (Kg)', kind: 'number', align: 'right', width: 155 }, // 29
  { key: 'allocating_bag', label: 'Allocating (Bag)', kind: 'number', align: 'right', width: 132 }, // 30
  { key: 'allocating_quantity', label: 'Allocating (Kg)', kind: 'number', align: 'right', width: 126 }, // 31
  { key: 'pending_bag', label: 'Pending (Bag)', kind: 'number', align: 'right', width: 122 }, // 32
  { key: 'pending_kg', label: 'Pending (Kg)', kind: 'number', align: 'right', width: 116 }, // 33
  { key: 'requested_provi', label: 'Provisional.Requested(Kg)', kind: 'number', align: 'right', width: 192 }, // 34
  { key: 'to_be_requested_provi', label: 'Provisional.TobeRequested(Kg)', kind: 'number', align: 'right', width: 220 }, // 35
  { key: 'requested_fix', label: 'Fixation/Outright.Requested(Kg)', kind: 'number', align: 'right', width: 224 }, // 36
  { key: 'to_be_requested_fix', label: 'Fixation/Outright.TobeRequested(Kg)', kind: 'number', align: 'right', width: 252 }, // 37
  { key: 'status', label: 'Status', filter: 'checkboxes', width: 110 }, // 38
  { key: 'person_updated', label: 'Updated Person', width: 210 }, // 39
  { key: 'date_updated', label: 'Updated Date', kind: 'datetime', width: 150 }, // 40
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: GoodAllocationListItem[], key: keyof GoodAllocationListItem): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

const cellValue = (row: GoodAllocationListItem, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (NAME_KEYS.has(col.key)) return titleCaseName(val)
  if (col.kind === 'int') return String(Math.round(Number(val)))
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  return String(val)
}

export default function ProcurementGoodAllocationListView({ filters }: Props) {
  const snack = useSnackbar()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const { canEdit } = usePagePermission()
  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()

  // Multi-row tick (Access checked_row) — Tick All / Truck Status / multi-Delete.
  const [tickedIds, setTickedIds] = useState<Set<number>>(new Set())
  const [confirm, setConfirm] = useState<{ message: string; ids: number[] } | null>(null)
  const [formState, setFormState] = useState<{ mode: 'new' | 'edit' | 'copy' } | null>(null)
  const [massUploadOpen, setMassUploadOpen] = useState(false)
  const [truckStatusRows, setTruckStatusRows] = useState<GoodAllocationListItem[] | null>(null)
  const [printRow, setPrintRow] = useState<GoodAllocationListItem | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof GoodAllocationListItem> | null>(null)
  const { getWidth, startResize } = useColumnWidths('procurement.good_allocation.list')

  const filterPopover = useFilterPopover<FilterKind>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof GoodAllocationListItem
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const columnsCtrl = usePreferenceColumns({
    scope: 'procurement',
    prefKey: 'columns',
    subKey: 'procurement-good-allocation',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'procurement.good_allocation.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const { data, isLoading, isFetching, error } = useListGoodAllocationsQuery(
    {
      latest_delivery_from: filters.startDate || undefined,
      latest_delivery_to: filters.endDate || undefined,
      is_forced_close:
        filters.isForcedClose === 'yes' ? true : filters.isForcedClose === 'no' ? false : undefined,
      page: 1,
      page_size: ALL_ROWS_PAGE_SIZE,
    },
    { refetchOnMountOrArgChange: true, skip: filters.startDate > filters.endDate },
  )

  const [deleteGoodAlc, { isLoading: deleteLoading }] = useDeleteGoodAllocationMutation()
  const [deleteMany, { isLoading: deleteManyLoading }] = useDeleteManyGoodAllocationsMutation()

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  // Access frmProcurementQryGoodAlc Form_Load → `DoCmd.GoToRecord , , acLast` =
  // record CUỐI. List ORDER BY id ASC ⇒ dòng CUỐI (id cao nhất). Auto-select +
  // cuộn xuống đáy (initialTopMostItemIndex bên dưới). Chạy 1 lần, không đè user.
  const autoSelectedRef = useRef(false)
  useEffect(() => {
    if (autoSelectedRef.current) return
    if (isFetching || isLoading) return
    if (sortedRows.length === 0) return
    autoSelectedRef.current = true
    if (selectedId == null) setSelectedId(sortedRows[sortedRows.length - 1].id)
  }, [isFetching, isLoading, sortedRows, selectedId, setSelectedId])

  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number')
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )

  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const minTableWidth = useMemo(
    () =>
      visibleColumns.reduce((acc, col) => {
        const key = String(col.key)
        const w = getWidth(key) ?? col.width ?? (FROZEN_KEYS.has(key) ? FROZEN_FALLBACK_WIDTH[key] : 100)
        return acc + w
      }, TICK_COL_WIDTH),
    [visibleColumns, getWidth, FROZEN_FALLBACK_WIDTH],
  )
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof GoodAllocationListItem) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<GoodAllocationListItem | null>(
    () => (selectedId == null ? null : rows.find((r) => r.id === selectedId) ?? null),
    [rows, selectedId],
  )

  // --- Tick helpers ---
  const toggleTick = (id: number) =>
    setTickedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const allFilteredTicked = filteredRows.length > 0 && filteredRows.every((r) => tickedIds.has(r.id))
  const tickedRows = useMemo(() => filteredRows.filter((r) => tickedIds.has(r.id)), [filteredRows, tickedIds])

  // Checkbox header (select-all): tick/untick toàn bộ dòng đang hiển thị (đã lọc).
  // Follow Pay Data — tick-all bằng checkbox header, không cần nút toolbar riêng.
  const handleTickAll = () => {
    setTickedIds((prev) => {
      const next = new Set(prev)
      if (allFilteredTicked) filteredRows.forEach((r) => next.delete(r.id))
      else filteredRows.forEach((r) => next.add(r.id))
      return next
    })
  }

  // Access: nút Edit/Copy/single-Delete/Print mở đầu bằng check Form![subForm]!id.
  const requireRow = (): GoodAllocationListItem | null => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return null
    }
    return selectedRow
  }

  // --- Toolbar handlers ---
  const handleNew = () => {
    if (!canEdit) return
    setFormState({ mode: 'new' })
  }
  const handleEdit = () => {
    if (!canEdit) return
    if (!requireRow()) return
    setFormState({ mode: 'edit' })
  }
  const handleCopy = () => {
    if (!canEdit) return
    if (!requireRow()) return
    setFormState({ mode: 'copy' })
  }
  // Access btnTruckStatus: ưu tiên các row đã tick; nếu không tick thì dùng row
  // đang focus (selectedRow) — cho phép chỉnh nhanh 1 dòng mà không cần tick.
  const handleTruckStatus = () => {
    if (!canEdit) return
    const rows = tickedRows.length > 0 ? tickedRows : selectedRow ? [selectedRow] : []
    if (rows.length === 0) {
      snack.warning('Please choose a record')
      return
    }
    setTruckStatusRows(rows)
  }
  const handleMassUpload = () => {
    if (!canEdit) return
    setMassUploadOpen(true)
  }
  // Access btnPrint: contract_no not null + m_truck_no LIKE 'BBGN%'.
  // Record không đủ điều kiện → disable nút Print BBGN.
  const canPrintBbgn =
    !!selectedRow &&
    !!selectedRow.contract_no &&
    String(selectedRow.m_truck_no ?? '').toUpperCase().startsWith('BBGN')

  const handlePrint = () => {
    const row = requireRow()
    if (!row) return
    if (!row.contract_no) {
      snack.warning('Contract_no must be not null')
      return
    }
    if (!String(row.m_truck_no ?? '').toUpperCase().startsWith('BBGN')) {
      snack.info('This receipt is not BBGN!')
      return
    }
    setPrintRow(row)
  }
  // Access btnDelete: nếu có row ticked → xóa nhiều; else xóa 1 (row đang chọn).
  const handleDelete = () => {
    if (!canEdit) return
    if (tickedRows.length > 0) {
      setConfirm({
        message: `Do you want to delete ${tickedRows.length} records ?`,
        ids: tickedRows.map((r) => r.id),
      })
      return
    }
    const row = requireRow()
    if (!row) return
    setConfirm({
      message: `Do you want to delete Goods Allocation with ID '${row.id}'?`,
      ids: [row.id],
    })
  }

  const runConfirm = async () => {
    if (!confirm) return
    const ids = confirm.ids
    try {
      if (ids.length === 1) await deleteGoodAlc(ids[0]).unwrap()
      else await deleteMany({ ids }).unwrap()
      setSelectedId(null)
      setTickedIds(new Set())
      snack.success('Successfully Deleted')
    } catch (e) {
      snack.error(getErrorMessage(e))
    } finally {
      setConfirm(null)
    }
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button variant="outlined" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={handleNew} startIcon={<AddIcon sx={{ fontSize: 16 }} />} title="New">
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={handleEdit} startIcon={<EditIcon sx={{ fontSize: 16 }} />} title="Edit">
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={handleCopy} startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />} title="Copy">
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
            </Button>
            <Button variant="outlined" size="small" color="secondary" sx={actionToolbarBtnSx} disabled={(tickedRows.length === 0 && !selectedRow) || !canEdit} onClick={handleTruckStatus} startIcon={<LocalShippingIcon sx={{ fontSize: 16 }} />} title="Truck Status">
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Truck Status</Box>
            </Button>
            <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={(!selectedRow && tickedRows.length === 0) || !canEdit || deleteLoading || deleteManyLoading} onClick={handleDelete} startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} title="Delete">
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
            </Button>
            <Button variant="outlined" size="small" color="secondary" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={handleMassUpload} startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />} title="Mass Upload">
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Mass Upload</Box>
            </Button>
            <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!canPrintBbgn} onClick={handlePrint} startIcon={<PrintIcon sx={{ fontSize: 16 }} />} title={canPrintBbgn ? 'Pr-BBGN' : 'Only records with BBGN receipt & contract can be printed'}>
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Pr-BBGN</Box>
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => filterPopover.open('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e: MouseEvent<HTMLElement>) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Could not load Goods Allocation list.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            {tickedRows.length > 0 && (
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>
                Ticked: {tickedRows.length}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Contract, supplier, truck, receipt, note…"
              label="Search Goods Allocation"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <DataframeBody loading={isLoading} fetching={isFetching}>
          {error ? (
            <BrandState tone="error" title="Could not load Goods Allocation list" description="Please refresh the page or contact the system administrator if the issue continues." />
          ) : isLoading ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState title="No Goods Allocation" description="Try adjusting the Delivery Latest date range or the table filters above." />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              initialTopMostItemIndex={rows.length > 0 ? { index: rows.length - 1, align: 'end' } : 0}
              fixedHeaderContent={() => (
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    className="app-table-header-cell app-sticky-col"
                    style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH, maxWidth: TICK_COL_WIDTH, left: 0 }}
                    sx={{ textAlign: 'center' }}
                  >
                    <Checkbox
                      size="small"
                      sx={{ p: 0 }}
                      checked={allFilteredTicked}
                      indeterminate={!allFilteredTicked && tickedRows.length > 0}
                      onChange={handleTickAll}
                      title="Tick all (filtered)"
                    />
                  </TableCell>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = TICK_COL_WIDTH
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className={isFrozen ? 'app-table-header-cell app-sticky-col-2' : 'app-table-header-cell'}
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', ...(isFrozen ? {} : { position: 'relative' }) }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  <TableCell padding="checkbox" className="app-sticky-col" style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH, maxWidth: TICK_COL_WIDTH, left: 0 }} sx={{ bgcolor: 'background.paper', borderTop: (theme) => `1px solid ${theme.palette.divider}` }} />
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col-2' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          ...(isFrozen ? { left: TICK_COL_WIDTH } : {}),
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: GoodAllocationListItem) => (
                <>
                  <TableCell
                    padding="checkbox"
                    className="app-sticky-col"
                    style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH, maxWidth: TICK_COL_WIDTH, left: 0 }}
                    sx={{ textAlign: 'center', py: 0.25 }}
                    onClick={(e) => { e.stopPropagation(); toggleTick(row.id) }}
                  >
                    <Checkbox size="small" sx={{ p: 0 }} checked={tickedIds.has(row.id)} onChange={() => toggleTick(row.id)} onClick={(e) => e.stopPropagation()} />
                  </TableCell>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const val = cellValue(row, col)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const wrap = col.wrap || getWidth(key) != null
                    const frozenStyle: CSSProperties = isFrozen ? { left: TICK_COL_WIDTH } : {}
                    if (wrap) {
                      return (
                        <TableCell
                          key={key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col-2' : undefined}
                          style={{ ...(w ? { width: w, minWidth: w, maxWidth: w } : {}), ...frozenStyle }}
                          sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                        >
                          <Box component="span" className="app-table-cell-wrap">{val || '-'}</Box>
                        </TableCell>
                      )
                    }
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col-2' : undefined}
                        style={{ ...(w ? { width: w, minWidth: w, maxWidth: w } : {}), ...frozenStyle }}
                        sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  })}
                </>
              )}
              components={{
                Table: (props) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: object) => <TableHead {...props} component="thead" />) as never,
                TableBody: ((props: object) => <TableBody {...props} component="tbody" />) as never,
                TableRow: (props) => {
                  const idx = Number((props as unknown as Record<string, unknown>)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={row != null && row.id === selectedId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (row) toggleSelect(row.id)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </DataframeBody>
      </Box>

      <FuturesColumnsMenu controller={columnsCtrl} />

      <ConfirmDialog
        open={!!confirm}
        title="Delete Goods Allocate"
        message={confirm?.message ?? ''}
        onConfirm={() => void runConfirm()}
        onClose={() => setConfirm(null)}
      />

      {formState && (
        <GoodAllocationFormDialog
          open
          mode={formState.mode}
          goodAllocationId={formState.mode === 'new' ? null : selectedRow?.id ?? null}
          canEdit={canEdit}
          onClose={() => setFormState(null)}
          onSaved={() => setSelectedId(null)}
        />
      )}

      <GoodAllocationMassUploadDialog
        open={massUploadOpen}
        canEdit={canEdit}
        onClose={() => setMassUploadOpen(false)}
        onSaved={() => setTickedIds(new Set())}
      />

      <GoodAllocationTruckStatusDialog
        open={truckStatusRows != null}
        rows={truckStatusRows ?? []}
        canEdit={canEdit}
        onClose={() => setTruckStatusRows(null)}
        onSaved={() => setTickedIds(new Set())}
      />

      <GoodAllocationBbgnPrintDialog
        open={printRow != null}
        row={printRow}
        onClose={() => setPrintRow(null)}
      />
    </Box>
  )
}
